// @flow
import * as React from 'react';
import {
    Box, Button, Text, Stack, TextInput, Heading,
} from 'grommet';
import {
    FormAdd, Duplicate, Trash,
} from 'grommet-icons';

import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { FieldWrapper } from 'speed-js-react-web';

import styled from 'styled-components';

import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import CableFiberColorSchemeField from 'esm-web-pro/containers/pages/network/forms/fields/CableColorSchemeFiberField';


type Fiber = {
    id: number,
    tubeNumber: number,
    color: string,
    stripe_count: number,
}

type Tube = {
    id: number,
    fibers: Array<Fiber>,
}


const messages = defineMessages({
    tube: {
        id: 'tube',
        defaultMessage: 'Tube',
    },
});

type CableModelColorSchemeFormProps = {
    values: Object,
    isEdit: boolean,
    initialValues?: Array<Tube>,
    onSubmit: Function,
    onEdit: Function,
    onClose: Function,
}


const CableModelColorSchemeForm = ({
    values, initialValues, isEdit, onSubmit, onEdit, onClose,
}: CableModelColorSchemeFormProps) => {
    const { formatMessage } = useIntl();

    // Get he digits after the last underscore
    const totalFibers = parseInt(values.fibers_count.split('_').pop(), 10);
    const [usedFiberCount, setUsedFiberCount] = React.useState(0);

    const [canAddFiber, setCanAddFiber] = React.useState(true);
    const [formIsValid, setFormIsValid] = React.useState(false);

    const [showButtonShadow, setShowButtonShadow] = React.useState(true);

    const defaultFiberColor = '#0074d9';

    const fibersPerTube = Math.floor(totalFibers / values.tube_count);

    const [tubes, setTubes] = React.useState([]);


    const changeFiberColor = React.useCallback((tubeIndex, fiberIndex, color) => {
        const _tubes = [...tubes];
        // Copy the fibers array so we don't mutate it directly
        const tubeFibers = [..._tubes[tubeIndex].fibers];

        tubeFibers[fiberIndex].color = color;
        _tubes[tubeIndex].fibers = tubeFibers;
        setTubes(_tubes);
    }, [tubes]);

    const changeFiberStripeCount = React.useCallback((tubeIndex, fiberIndex, stripeCount) => {
        const _tubes = [...tubes];
        const tubeFibers = [..._tubes[tubeIndex].fibers];

        tubeFibers[fiberIndex].stripe_count = stripeCount;
        _tubes[tubeIndex].fibers = tubeFibers;
        setTubes(_tubes);
    }, [tubes]);

    const validateForm = () => {
        // If we have a tube without fibers, the form is invalid, or if we have a fiber without a color or stripe count
        const tubesAreValid = tubes.every((tube) => {
            if (tube.fibers.length === 0) {
                return false;
            }

            tube.fibers.every((fiber) => {
                if (fiber.color === '' || fiber.stripe_count === '') {
                    return false;
                }
                return true;
            });


            return true;
        });

        return tubesAreValid;
    };


    // We just append a new fiber to the tube
    const addFiberToTube = (tubeIndex, color, stripeCount) => {
        const _tubes = [...tubes];
        const tubeFibers = [..._tubes[tubeIndex].fibers];

        const newFiber = {
            id: tubeFibers.length + 1,
            color,
            stripe_count: stripeCount,
        };
        tubeFibers.push(newFiber);

        _tubes[tubeIndex].fibers = tubeFibers;

        setTubes(_tubes);
        setUsedFiberCount(usedFiberCount + 1);

        if (usedFiberCount + 1 === totalFibers) {
            setCanAddFiber(false);
        }
    };

    const removeFiberFromTube = (tubeIndex) => {
        const _tubes = [...tubes];
        const tubeFibers = [..._tubes[tubeIndex].fibers];
        // Remove last fiber from tube
        tubeFibers.pop();
        _tubes[tubeIndex].fibers = tubeFibers;
        setTubes(_tubes);
        setUsedFiberCount(usedFiberCount - 1);

        if (usedFiberCount - 1 < totalFibers) {
            setCanAddFiber(true);
        }
    };

    const clearTubeFibers = (tubeIndex) => {
        const _tubes = [...tubes];
        const tubeFibers = [..._tubes[tubeIndex].fibers];

        setUsedFiberCount(usedFiberCount - tubeFibers.length);
        if (usedFiberCount - tubeFibers.length < totalFibers) {
            setCanAddFiber(true);
        }
        _tubes[tubeIndex].fibers = [];
        setTubes(_tubes);
    };

    const clonePreviousTubeFibers = (tubeIndex) => {
        if (tubeIndex === 0) return;

        const _tubes = [...tubes];
        const tubeFibers = [..._tubes[tubeIndex].fibers];
        // If we already have fibers in the tube, we don't want to clone the previous tube
        if (tubeFibers.length > 0) return;

        // We need to create a new array, otherwise the fiber values will be shared between the tubes
        const previousTubeFibers = [..._tubes[tubeIndex - 1].fibers];
        const newFibers = previousTubeFibers.map((fiber) => ({
            id: fiber.id,
            color: fiber.color,
            // If the fiber has no stripe count, we set it to 0
            stripe_count: fiber.stripe_count || 0,
        }));

        const newTubeFiberCount = usedFiberCount + newFibers.length;
        if (newTubeFiberCount > totalFibers) {
            // Remove fibers from the end of the array until we have the total number of fibers
            const difference = newTubeFiberCount - totalFibers;
            newFibers.splice(newFibers.length - difference);
        }

        _tubes[tubeIndex].fibers = newFibers;
        setTubes(_tubes);
        setUsedFiberCount(usedFiberCount + newFibers.length);

        if (usedFiberCount + _tubes[tubeIndex].fibers.length === totalFibers) {
            setCanAddFiber(false);
        }
    };


    const handleSchemeSubmit = () => {
        if (isEdit) {
            onEdit(tubes);
        } else {
            onSubmit(tubes);
        }
    };

    // Hide the button shadow when the user scrolls to the bottom of the list
    const handleScroll = () => {
        const element = document.getElementById('end-of-tube-list');
        if (element) {
            const rect = element.getBoundingClientRect();
            const isVisible = (rect.top >= 0) && (rect.bottom <= window.innerHeight);
            setShowButtonShadow(!isVisible);
        }
    };


    React.useEffect(() => {
        const _tubes = [];
        let totalFiberCount = 0;

        if (initialValues.length > 0) {
            setTubes(initialValues);
            totalFiberCount = initialValues.reduce((acc, tube) => acc + tube.fibers.length, 0);
        } else {
            for (let i = 0; i < values.data.tube_count; i += 1) {
                const _fibers: Fiber = [];
                // Set the initial fibers only for the first tube
                if (i === 0) {
                    // We need to subtract 1 because we start counting from 0 in the loop
                    let fiberCount = fibersPerTube - 1;

                    // If we only have one tube we want to add all the fibers to it
                    if (values.data.tube_count === 1) {
                        fiberCount = totalFibers - 1;
                    }

                    for (let j = 0; j <= fiberCount; j += 1) {
                        _fibers.push({
                            id: j + 1,
                            color: defaultFiberColor,
                            stripe_count: 0,
                        });
                        totalFiberCount += 1;
                    }
                }

                _tubes.push({
                    id: i + 1,
                    fibers: _fibers,
                });
            }
            setTubes(_tubes);
        }

        setUsedFiberCount(totalFiberCount);
        if (totalFiberCount === totalFibers) {
            setCanAddFiber(false);
            setFormIsValid(true);
        }
    }, []);


    return (
        <Box
            height="100%"
            background={{ color: 'white' }}
            overflow="auto"
            position="relative"
            display="block"
            onScroll={handleScroll}
        >

            <StyledContentDiv>
                <FieldWrapper
                    label={(<FormattedMessage id="cable-attributes-fiber-count" defaultMessage="Fiber Count" />)}
                >
                    <Stack anchor="right">
                        <TextInput
                            disabled
                            name="fibers_count"
                            value={totalFibers}
                        />
                    </Stack>
                </FieldWrapper>


                <FieldWrapper
                    label={(<FormattedMessage id="cable-attributes-tube-count" defaultMessage="Tube Count" />)}
                >
                    <Stack anchor="right">
                        <TextInput
                            disabled
                            name="tube_count"
                            type="number"
                            value={values.tube_count}
                        />
                    </Stack>
                </FieldWrapper>

                <Box style={{ paddingBottom: '2rem' }} />
                <hr style={{
                    color: 'grey',
                    backgroundColor: 'grey',
                    width: '100%',
                    display: 'flex',
                }}
                />
                {tubes.map((tube, tubeIndex) => {
                    const tubeId = tube.id;
                    const tubeFiberCount = tube.fibers;

                    return (
                        <Box key={`${values.id}-${tubeId}`} flex={false}>
                            <Box direction="row" align="center" justify="between">
                                <StyledHeading margin="none">
                                    {`${formatMessage(messages.tube)} ${tubeId}`}
                                </StyledHeading>
                                <Box direction="row" align="end" gap="small" style={{ alignItems: 'center' }}>
                                    {(tubeIndex !== 0) && (
                                        <Button
                                            style={{ padding: 4 }}
                                            icon={<Duplicate size="small" />}
                                            plain={false}
                                            onClick={() => clonePreviousTubeFibers(tubeIndex)}
                                            disabled={
                                                // If we already have fibers in the current
                                                // tube we can't clone the previous tube
                                                tube.fibers.length > 0
                                                // If we can't add fibers we can't clone the previous tube
                                                || !canAddFiber
                                                // If the previous tube has no fibers we can't clone it
                                                || tubes[tubeIndex - 1].fibers.length === 0
                                            }
                                            label={(
                                                <Text style={{ fontSize: '14px' }}>
                                                    <FormattedMessage
                                                        id="clone-previous-tube"
                                                        defaultMessage="Clone"
                                                    />
                                                </Text>
                                            )}
                                        />
                                    )}
                                    <Button
                                        style={{ padding: 5 }}
                                        icon={<Trash size="small" />}
                                        plain={false}
                                        onClick={() => clearTubeFibers(tubeIndex)}
                                    />
                                </Box>
                            </Box>

                            <StyledHorizontalLine />


                            {tubeFiberCount.map((fiber, fiberIndex) => (
                                <CableFiberColorSchemeField
                                    key={`tube-${tubeId}-${fiber.id}`}
                                    tubeIndex={tubeIndex}
                                    fiber={fiber}
                                    fiberIndex={fiberIndex}
                                    removeFiberFromTube={removeFiberFromTube}
                                    changeFiberColor={changeFiberColor}
                                    changeFiberStripeCount={changeFiberStripeCount}
                                    clearTubeFibers={clearTubeFibers}
                                />

                            ))}

                            {tubeFiberCount.length === 0 && (
                                <Box pad="small">
                                    <Text color="status-error" alignSelf="center">
                                        <FormattedMessage
                                            id="tube-must-have-fiber"
                                            defaultMessage="Tube must have at least one fiber."
                                        />
                                    </Text>
                                </Box>
                            )}

                            <Box align="center">
                                <Button
                                    style={{ padding: 0 }}
                                    plain={false}
                                    onClick={() => addFiberToTube(tubeIndex, defaultFiberColor, 0)}
                                    disabled={!canAddFiber}
                                >
                                    <Box
                                        direction="row"
                                        align="center"
                                        style={{ padding: '5px' }}
                                    >
                                        <FormAdd size="medium" />
                                        <Text style={{ fontSize: '14px' }}>
                                            <FormattedMessage
                                                id="add-fiber"
                                                defaultMessage="Add Fiber"
                                            />
                                        </Text>
                                    </Box>

                                </Button>
                                {!canAddFiber && (
                                    <Text
                                        color="status-message"
                                        alignSelf="center"
                                        style={{ paddingTop: '0.5rem' }}
                                    >
                                        <FormattedMessage
                                            id="no-fibers-available"
                                            defaultMessage="No more fibers available."
                                        />
                                    </Text>
                                )}
                            </Box>


                            <StyledHorizontalLine />
                        </Box>
                    );
                })}
                <Box id="end-of-tube-list" style={{ paddingBottom: '56px' }} />

            </StyledContentDiv>
            <StyledActionBox shadow={showButtonShadow}>
                <ActionButtons
                    onClose={onClose}
                    onSave={handleSchemeSubmit}
                    disabled={
                        canAddFiber
                        // IF any of the tubes have no fibers we can't save
                        || tubes.some((tube) => tube.fibers.length === 0)
                    }
                />
            </StyledActionBox>
        </Box>
    );
};

CableModelColorSchemeForm.defaultProps = {
    initialValues: [],
};

const StyledContentDiv = styled.div`
  height: 100%;
  position: relative;
  padding-left: 1rem;
  padding-right: 1rem;
  

  &::before {
    content: "";
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }
`;

const StyledHeading = styled(Heading)`
    line-height: unset;
    white-space: nowrap;
    font-size: 16px;
  `;

export const StyledShadow = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    box-shadow: '0px 0px 10px #c0bfbf' ;
    width: inherit;
}
`;

const StyledActionBox = styled(Box)`
    width: 100%;
    height: 52px;
    position: absolute;
    background-color: #ffffff;
    bottom: 0;
    left: 0;
    box-shadow: ${(props) => (props.shadow ? '0px 0px 10px #c0bfbf' : 'none')};
`;

const StyledHorizontalLine = styled.hr`
    color: grey;
    background-color: grey;
    width: 100%;
    display: flex;
`;

export default CableModelColorSchemeForm;
